import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRentangTanggal } from "@/lib/datetime";
import { badanSimpan, jumlahTerisi, petakanGalat } from "@/lib/rotasiCepat";
import { NAMA_BULAN, hariTerisi, pesanHariPenuh, tandaKalender, teksPeringatan } from "@/lib/kalenderRotasi";
import { IsianKalenderRotasi, useIsianKalender } from "@/components/IsianKalenderRotasi";
import DaftarRotasiBaru from "@/components/DaftarRotasiBaru";
import KonfirmasiPeringatanRotasi from "@/components/KonfirmasiPeringatanRotasi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Panel "Input Rotasi Cepat" di bawah baris peserta (tab Per Peserta): SATU kalender utk Stase RSCM,
// Stase Luar (rentang: klik awal lalu akhir, pilih tempat, "Tambahkan ke daftar") dan Jaga (klik
// tanggal = nyala/mati) -- isian bersama dgn dialog Isi Massal (IsianKalenderRotasi). Hanya MENAMBAH;
// ubah/hapus rotasi tersimpan tetap lewat Edit Peserta > Rotasi Klinik. Simpan = POST
// /rotasi-klinik/bulk-create (semua atau tidak sama sekali); aturan ditegakkan server.
export default function RotasiInputCepatPanel({
    pesertaId, bulan, tahun, master, saranRs, locked, onTersimpan,
}) {
    const [info, setInfo] = useState(null);
    const [galat, setGalat] = useState({});
    const [peringatan, setPeringatan] = useState({});
    const [saving, setSaving] = useState(false);
    const [konfirmasi, setKonfirmasi] = useState(null); // { lanjut, peringatan }
    const bersihkanPesan = () => { setGalat({}); setPeringatan({}); };
    const isian = useIsianKalender({
        bulan, tahun, terpakai: info?.terpakai, maks: info?.maks, aturan: info?.aturan, onUbah: bersihkanPesan,
    });
    const { form, bln, thn } = isian;

    useEffect(() => {
        let batal = false;
        setInfo(null);
        (async () => {
            try {
                const { data } = await api.get("/rotasi-klinik/input-cepat", { params: { peserta_id: pesertaId, bulan, tahun } });
                if (batal) return;
                setInfo(data);
                setGalat({});
                setPeringatan({});
            } catch (e) {
                if (!batal) toast.error(formatApiErrorDetail(e.response?.data?.detail));
            }
        })();
        return () => { batal = true; };
    }, [pesertaId, bulan, tahun]);

    const tanda = useMemo(() => tandaKalender(info?.kalender, form), [info, form]);
    const terisi = useMemo(() => hariTerisi(info?.kalender, form, bln, thn), [info, form, bln, thn]);

    if (!info || !isian.kuota) return <div className="p-4 text-xs text-slate-500">Memuat rotasi peserta...</div>;

    const namaBulan = `${NAMA_BULAN[bln - 1]} ${thn}`;
    const { kuota } = isian;

    const tampilkanGalat = (e) => {
        const detail = e.response?.data?.detail;
        if (detail?.baris) setGalat(petakanGalat(form, detail.baris));
        toast.error(formatApiErrorDetail(detail));
    };
    const kirim = async (lanjut) => {
        setSaving(true);
        try {
            const { data } = await api.post("/rotasi-klinik/bulk-create", badanSimpan(form, pesertaId, bulan, tahun));
            toast.success(`${data.dibuat} segmen rotasi tersimpan`);
            onTersimpan(lanjut);
        } catch (e) {
            tampilkanGalat(e);
        } finally {
            setSaving(false);
        }
    };
    const simpan = async (lanjut) => {
        bersihkanPesan();
        setSaving(true);
        try {
            const { data } = await api.post("/rotasi-klinik/bulk-create", badanSimpan(form, pesertaId, bulan, tahun, true));
            if (data.peringatan?.length) {
                setPeringatan(petakanGalat(form, data.peringatan, "pesan"));
                setKonfirmasi({ lanjut, peringatan: data.peringatan });
                setSaving(false);
                return;
            }
        } catch (e) {
            tampilkanGalat(e);
            setSaving(false);
            return;
        }
        await kirim(lanjut);
    };

    const adaIsian = jumlahTerisi(form) > 0;

    return (
        <div className="relative p-3 space-y-3 bg-slate-50 border-t border-slate-200" data-testid="rc-panel">
            <datalist id="rc-rs-saran">{saranRs.map((n) => <option key={n} value={n} />)}</datalist>
            <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                    Rotasi tersimpan bulan ini (ubah/hapus lewat Edit Peserta &gt; Rotasi Klinik)
                </div>
                {info.tersimpan.length === 0 ? (
                    <div className="text-xs text-slate-400">Belum ada.</div>
                ) : (
                    <ul className="text-xs text-slate-700 space-y-0.5" data-testid="rc-tersimpan">
                        {info.tersimpan.map((s, i) => (
                            <li key={i}>
                                <span className="font-semibold">{s.jenis}</span> — {s.tempat}
                                <span className="text-slate-500"> ({s.tanggal_mulai || s.tanggal_akhir
                                    ? formatRentangTanggal(s.tanggal_mulai, s.tanggal_akhir) : "tanpa tanggal"})</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {locked ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="rc-terkunci">
                    Di luar jendela edit (Pengaturan Periode) — rotasi tidak bisa ditambahkan saat ini.
                </div>
            ) : (
                <>
                    <IsianKalenderRotasi isian={isian} master={master} tanda={tanda}>
                        <div className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 space-y-0.5" data-testid="rc-penghitung">
                            <div>Kuota {namaBulan}: <b>Stase {kuota.stase.terpakai}/{kuota.stase.maks}</b> · <b>Jaga {kuota.jaga.terpakai}/{kuota.jaga.maks}</b></div>
                            <div>Hari terisi {namaBulan}: Stase {terisi.stase} hari · Jaga {terisi.jaga} hari</div>
                        </div>
                    </IsianKalenderRotasi>

                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Akan disimpan</div>
                        <div className="max-h-72 overflow-y-auto pr-1" data-testid="rc-area-daftar">
                            <DaftarRotasiBaru form={form} master={master} galat={galat} peringatan={peringatan}
                                ambang={info.aturan?.ambang_hari_penuh} onHapus={isian.hapus} />
                        </div>
                    </div>
                    <p className="text-[11px] text-slate-500">
                        Stase RSCM + Stase Luar maksimal {info.maks.stase} per bulan, Jaga maksimal {info.maks.jaga}.
                        Tumpang tindih Stase dan penempatan hampir sebulan penuh hanya diberi peringatan.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-end">
                        <Button type="button" variant="outline" disabled={saving || !adaIsian} onClick={() => simpan(true)}
                            data-testid="rc-simpan-berikutnya">
                            Simpan &amp; berikutnya
                        </Button>
                        <Button type="button" disabled={saving || !adaIsian} onClick={() => simpan(false)}
                            className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="rc-simpan">
                            {saving ? "Menyimpan..." : "Simpan"}
                        </Button>
                    </div>
                </>
            )}

            <KonfirmasiPeringatanRotasi
                open={!!konfirmasi}
                baris={(konfirmasi?.peringatan || []).map(teksPeringatan)}
                pesanHariPenuh={pesanHariPenuh(konfirmasi?.peringatan)}
                onBatal={() => setKonfirmasi(null)}
                onLanjut={() => { const k = konfirmasi; setKonfirmasi(null); kirim(k.lanjut); }}
            />
        </div>
    );
}
